import SchoolAPI from "../apis/SchoolAPI";
import AlertService from "./AlertService";
import IsirService from "./IsirService";
import { createAndDownloadBlobFile, base64ToArrayBuffer } from "../utilities/FileUtility";

class SchoolService {
    // School Config methods
    async getConfigItemsBySchoolCode(schoolCode: string): Promise<SchoolConfigItem[]> {
        const resp = await SchoolAPI.get(
            "schoolconfig/getSchoolConfigItemsBySchoolCode/" + schoolCode
        );
        return resp.data;
    }

    async getConfigItemByName(key: string): Promise<SchoolConfigItem | undefined | null> {
        try {
            const resp = await SchoolAPI.get(
                "schoolconfig/getSchoolConfigItemByName/" + key
            );

            if (resp.status === 204) {
                return null;
            } else {
                return resp.data;
            }
        } catch (err) {
            console.log(err);
        }
    }

    async getBetaTestToggleConfig(key: string): Promise<SchoolConfigItem | undefined | null> {
        try {
            const resp = await SchoolAPI.get(
                "schoolconfig/getBetaTestToggleConfig/" + key
            );

            if (resp.status === 204) {
                return null;
            } else {
                return resp.data;
            }
        } catch (err) {
            console.log(err);
        }
    }

    async updateConfigItem(id: number, item: SchoolConfigItem): Promise<boolean> {
        try {
            const resp = await SchoolAPI.patch("schoolconfig/" + id, item);
            return resp.data;
        } catch (err) {
            AlertService.addErrorAlert("Update config failed");
            return false;
        }
    }

    async addConfigItem(item: SchoolConfigItem): Promise<SchoolConfigItem | boolean> {
        try {
            const resp = await SchoolAPI.post("schoolconfig/", item);
            return resp.data;
        } catch (err) {
            AlertService.addErrorAlert("Update config failed");
            return false;
        }
    }

    async getAllConfigItem(): Promise<SchoolConfigItem[] | undefined> {
        try {
            const resp = await SchoolAPI.get("schoolconfig/");
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    }

    // School methods
    async getSchool(schoolCode: string): Promise<School | undefined> {
        try {
            const resp = await SchoolAPI.get("school/getBySchoolCode/" + schoolCode);
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    }

    async updateSchool(schoolID: number, schoolValues: School): Promise<boolean> {
        try {
            const resp = await SchoolAPI.patch(`school/allowNullUpdate/${schoolID}`, schoolValues);
            AlertService.addSuccessAlert("Successfully Updated School");
            return resp.data;
        } catch (err) {
            AlertService.addErrorAlert("Update school failed");
            return false;
        }
    }

    async getAllSchoolIsirRecordInfo(): Promise<SchoolIsirRecordInfo[] | undefined> {
        try {
            const resp = await SchoolAPI.get(
                "/schoolIsirRecordInfo/"
            );
            return resp.data;
        } catch (err) { }
    }

    async getAvailableAidYears(schoolCode: string): Promise<number[] | undefined> {
        try {
            const resp = await SchoolAPI.get(
                "/schoolIsirRecordInfo/getAidYearsBySchoolCode/" + schoolCode
            );
            return resp.data;
        } catch (err) { }
    }

    // SchoolIsirRecInfo methods
    async getSchoolIsirRecInfo(schoolCode: string, aidYear: number) : Promise<SchoolIsirRecordInfo | undefined> {
        try {
            const resp = await SchoolAPI.get(
                "schoolIsirRecordInfo/getBySchoolCodeAndAidYear/" +
                schoolCode +
                "/" +
                aidYear
            );
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    }

    async addSchoolIsirRecInfo(schoolIsirRecordInfo: SchoolIsirRecordInfo) : Promise<SchoolIsirRecordInfo | undefined> {
        try {
            const resp = await SchoolAPI.post(
                `schoolIsirRecordInfo/`,
                schoolIsirRecordInfo
            );
            AlertService.addSuccessAlert("School Aid Year Added");
            return resp.data;
        } catch (err) {
            AlertService.addErrorAlert("School Aid Year could not be added");
        }
    }

    async updateSchoolIsirRecInfo(id: number, schoolIsirRecInfoValues: SchoolIsirRecordInfo) : Promise<void> {
        try {
            await SchoolAPI.patch(
                "schoolIsirRecordInfo/" + id,
                schoolIsirRecInfoValues
            );
            AlertService.addSuccessAlert("Successfully Updated School");
        } catch (err) { }
    }

    async getSchoolIsirRecInfoVGroupMap(schoolIsirRecInfoID: number): Promise<SchoolIsirRecordInfoVGroupMap[] | undefined> {
        try {
            const resp = await SchoolAPI.get("schoolIsirRecInfoVGroupMap/getBySchoolIsirRecordInfoID/" + schoolIsirRecInfoID);
            return resp.data;
        } catch (err) { }
    }

    async addSchoolIsirRecInfoVGroupMap(schoolIsirRecInfoVGroupMap: SchoolIsirRecordInfoVGroupMap): Promise<SchoolIsirRecordInfoVGroupMap | undefined> {
        try {
            const resp = await SchoolAPI.post(`schoolIsirRecInfoVGroupMap/`, schoolIsirRecInfoVGroupMap);
            return resp.data;
        } catch (err) { }
    }

    async pollMailboxes(mailboxNumbers: string[]): Promise<string | boolean> {
        try {
            const resp = await SchoolAPI.post(`saigAdmin/pollMailboxes`, mailboxNumbers);
            return resp.data;
        } catch (err) {
            AlertService.addErrorAlert(
                "There was an issue connecting to the SAIG Mailbox. Please retry at a later time."
            );
            console.log(err);
            return false;
        }
    }

    async createSchoolFolder(tgNumber: string): Promise<string | undefined> {
        try {
            const resp = await SchoolAPI.post(
                `saigAdmin/createSchoolFolder/${tgNumber}`
            );
            AlertService.addSuccessAlert("Setup School");
            return resp.data;
        } catch (err) {
            AlertService.addErrorAlert("School folder could not be created");
            console.log(err);
        }
    }

    async getSchoolFolder(tgNumber: string): Promise<string | undefined> {
        try {
            const resp = await SchoolAPI.get(`saigAdmin/getSchoolFolder/${tgNumber}`);
            AlertService.addSuccessAlert("School folder exists");
            return resp.data;
        } catch (err) {
            AlertService.addErrorAlert("Could not find school folder");
            console.log(err);
        }
    }

    async checkIfPasswordExists(tgNumber: string): Promise<string | undefined> {
        try {
            const resp = await SchoolAPI.get(
                `saigAdmin/checkIfPasswordExists/${tgNumber}`
            );
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    }

    async setPassword(tgNumber: string, password: string): Promise<string | undefined> {
        try {
            const resp = await SchoolAPI.post(`saigAdmin/storePassword/${tgNumber}`, {
                newPassword1: password,
            });
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    }

    async updatePassword(tgNumber: string, password: string): Promise<string | undefined> {
        try {
            const resp = await SchoolAPI.post(
                `saigAdmin/updatePassword/${tgNumber}`,
                { newPassword1: password }
            );
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    }

    async setupPassword(tgNumber: string, currentPassword: string, newPassword: string): Promise<string | undefined> {
        try {
            const resp = await SchoolAPI.post(`saigAdmin/setupPassword/${tgNumber}`, {
                oldPassword: currentPassword,
                newPassword1: newPassword,
            });
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    }

    async getSchoolTgInfo(schoolCode: string): Promise<SchoolTgInfo | undefined> {
        try {
            const resp = await SchoolAPI.get(
                `saigAdmin/getSchoolTgInfo/${schoolCode}`
            );
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    }

    async updateTgInfo(saigEntity: SchoolTgInfo): Promise<void> {
        try {
            const resp = await SchoolAPI.patch(`saigAdmin/updateTgInfo`, saigEntity);
            AlertService.addSuccessAlert("Successfully updated mailbox settings.");
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    }

    async getAllEdMessages(): Promise<EdMessage[] | undefined> {
        try {
            const resp = await SchoolAPI.get(`edMessage`);
            AlertService.addSuccessAlert("Successfully retrieved mailbox.");
            return resp.data;
        } catch (err: any) {
            console.log(err.message);
        }
    }

    async downloadSaigFile(tgNumber: string, fileID: string): Promise<string | undefined> {
        try {
            const resp = await SchoolAPI.get(
                `SaigAdmin/downloadFile/${encodeURIComponent(
                    tgNumber
                )}/${encodeURIComponent(fileID)}`
            );
            const arrayBuffer = base64ToArrayBuffer(resp.data);
            createAndDownloadBlobFile(arrayBuffer, fileID);
            return resp.data;
        } catch (err: any) {
            AlertService.addErrorAlert(err.message);
        }
    }

    async getSaigFtpLog(tgNumber: string): Promise<string | undefined> {
        try {
            const resp = await SchoolAPI.get(
                `SaigAdmin/getFTPLog/${encodeURIComponent(tgNumber)}`
            );
            return resp.data;
        } catch (err: any) {
            AlertService.addErrorAlert(err.message);
        }
    }

    async processSaigIsirFile(edMessage: EdMessage, schoolCode: string, aidYear: string) {
        try {
            // Uncomment the lines below if testing on development. They grab a known file in place of calling a SAIG Mailbox
            // const arrayBuffer = await InterviewService.downloadLink(
            //   1,
            //   "workspace://SpacesStore/ced22423-1f93-416d-996a-1f6e84dfe6c8;1.0",
            //   "lessFunISIR19-20.txt"
            // );

            // Call the mailbox and get back the file
            const file = await SchoolAPI.get(
                `SaigAdmin/downloadFile/${encodeURIComponent(
                    edMessage.tgNumber
                )}/${encodeURIComponent(edMessage.uniqueFileName)}`
            );
            const arrayBuffer = base64ToArrayBuffer(file.data);

            const formData = new FormData();
            formData.append("SchoolCode", schoolCode);
            formData.append("AidYear", aidYear);
            var blob = new Blob([arrayBuffer], {
                type: "text/plain",
            });
            formData.append("isirFile", blob, edMessage.uniqueFileName);
            const processed = await IsirService.processIsirFile(formData);
            if (processed) {
                // set ed file status to imported
                edMessage.edFileStatus = 1;
                await this.updateEdMessage(edMessage.id, edMessage);
            }
            return processed;
        } catch (err: any) {
            AlertService.addErrorAlert(err.message);
        }
    }

    async updateEdMessage(id: number, edMessage: EdMessage): Promise<void> {
        try {
            await SchoolAPI.patch(`edMessage/` + id, edMessage);
            AlertService.addSuccessAlert("File Imported Successfully");
        } catch (err) {
            console.log(err);
            return;
        }
    }

    async addHoliday(holiday: Holiday): Promise<Holiday | undefined> {
        try {
            const resp = await SchoolAPI.post(`holiday/`, holiday);
            AlertService.addSuccessAlert("Holiday Added");
            return resp.data;
        } catch (err) {
            AlertService.addErrorAlert("Holiday could not be added");
        }
    }

    async getAllHoliday(): Promise<Holiday[] | undefined> {
        try {
            const resp = await SchoolAPI.get("holiday/");
            return resp.data;
        } catch (err) {
            console.log(err);
            return;
        }
    }

    async deleteHoliday(id: number): Promise<void> {
        try {
            await SchoolAPI.delete(`holiday/${id}`);
            AlertService.addSuccessAlert("Holiday Deleted");
        } catch (err) {
            console.log(err);
            return;
        }
    }

    async updateHoliday(id: number, holiday: Holiday): Promise<void> {
        try {
            await SchoolAPI.patch(`holiday/` + id, holiday);
            AlertService.addSuccessAlert("Holiday Updated");
        } catch (err) {
            console.log(err);
            return;
        }
    }

    async copyDefaultHoliday(year: number): Promise<void> {
        try {
            await SchoolAPI.post("holiday/copyDefaultHoliday/" + year);
            AlertService.addSuccessAlert("Default Holidays Copied");
        } catch (err) {
            console.log(err);
            return;
        }
    }

    getAllPermissions = async (): Promise<Permission[] | undefined> => {
        try {
            const resp = await SchoolAPI.get(`permission/`);
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    };

    searchPermissions = async (permission: Permission):Promise<Permission[] | undefined> => {
        try {
            const resp = await SchoolAPI.post(`permission/search`, permission);
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    };

    getAllRoles = async (): Promise<Role[] | undefined> => {
        try {
            const resp = await SchoolAPI.get(`role/`);
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    };

    getNestedCategories = async (): Promise<NestedCategory[] | undefined> => {
        try {
            const resp = await SchoolAPI.get(`category/GetNestedCategories`);
            return resp.data;
        } catch (err: any) {
            console.log(err.message);
        }
    };

    getAllActivePermissions = async (): Promise<RolePermission[] | undefined> => {
        try {
            const resp = await SchoolAPI.get(`role/getAllActivePermissions`);
            return resp.data;
        } catch (err: any) {
            console.log(err.message);
        }
    };

    getAllRolePermissionLinks = async (): Promise<RolePermissionLink[] | undefined> => {
        try {
            const resp = await SchoolAPI.get(`RolePermissionLink`);
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    };

    batchAddRolePermissionLinks = async (addLinks: RolePermissionLink[]): Promise<number[] | undefined> => {
        try {
            const resp = await SchoolAPI.post(
                `RolePermissionLink/batchAddRolePermissionLinks`,
                addLinks
            );
            return resp.data;
        } catch (err: any) {
            console.log(err.message);
        }
    };

    batchDeleteRolePermissionLinks = async (deleteLinks: RolePermissionLink[]): Promise<number[] | undefined> => {
        try {
            const resp = await SchoolAPI.patch(
                `RolePermissionLink/batchDeleteRolePermissionLinks`,
                deleteLinks
            );
            return resp.data;
        } catch (err: any) {
            console.log(err.message);
        }
    };

    addRolePermissionLink = async (roleId: number, permissionId: number): Promise<RolePermissionLink | undefined> => {
        try {
            const resp = await SchoolAPI.post(`RolePermissionLink/`, {
                roleID: roleId,
                permissionID: permissionId,
            });
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    };

    deleteRolePermissionLink = async (id: number): Promise<void> => {
        try {
            await SchoolAPI.delete(`RolePermissionLink/${id}`);
        } catch (err) {
            console.log(err);
        }
    };

    getAverageSignupToVerifiedTime = async (aidYear: number): Promise<number | undefined> => {
        try {
            const resp = await SchoolAPI.get(`report/getAverageSignupToVerifiedTime/${aidYear}/`);
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    };

    getAverageReviewToVerifiedTime = async (aidYear: number): Promise<number | undefined> => {
        try {
            const resp = await SchoolAPI.get(`report/getAverageReviewToVerifiedTime/${aidYear}`);
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    };

    searchSchoolAidYearFile = async (
        schoolCode: string,
        aidYear: number,
        fileType: number
    ): Promise<SchoolAidYearFile[] | undefined> => {
        try {
            const resp = await SchoolAPI.post("SchoolAidYearFile/search/", {
                schoolCode: schoolCode,
                aidYear: aidYear,
                fileType: fileType,
            });
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    };

    async getSchoolFile(schoolCode: string, fileType: number, fileDownload: boolean): Promise<string | undefined | boolean> {
        try {
            const resp = await SchoolAPI.post("SchoolService/fileDownload?aidYearSpecific=false", {
                schoolCode: schoolCode,
                fileType: fileType,
            });

            if (fileDownload) {
                const arrayBuffer = base64ToArrayBuffer(resp.data.downloadResult);
                createAndDownloadBlobFile(arrayBuffer, resp.data.fileName);
            } else {
                return resp.data.downloadResult;
            }
        } catch (err: any) {
            // Do not throw error if downloading a logo
            if (fileType !== 0) {
                AlertService.addErrorAlert(err.response.data);
            }
            return false;
        }
    };

    getSchoolAidyearFile = async (
        schoolCode: string,
        aidYear: string,
        fileType: number,
        fileDownload: boolean
    ): Promise<string | undefined | boolean> => {
        try {
            const resp = await SchoolAPI.post("SchoolService/fileDownload/", {
                schoolCode: schoolCode,
                aidYear: parseInt(aidYear),
                fileType: fileType,
            });

            if (fileDownload) {
                const arrayBuffer = base64ToArrayBuffer(resp.data.downloadResult);
                createAndDownloadBlobFile(arrayBuffer, resp.data.fileName);
            } else {
                return resp.data.downloadResult;
            }
        } catch (err: any) {
            // Do not throw error if downloading a logo
            if (fileType !== 0) {
                AlertService.addErrorAlert(err.response.data);
            }
            return false;
        }
    };

    getSchoolAidyearFileExact = async (
        schoolCode: string,
        aidYear: number,
        fileType: number,
        fileDownload: boolean
    ): Promise<string | undefined | boolean> => {
        try {
            const resp = await SchoolAPI.post(
                "SchoolService/fileDownloadExactYear/",
                {
                    schoolCode: schoolCode,
                    aidYear: aidYear,
                    fileType: fileType,
                }
            );

            if (fileDownload) {
                const arrayBuffer = base64ToArrayBuffer(resp.data.downloadResult);
                createAndDownloadBlobFile(arrayBuffer, resp.data.fileName);
            } else {
                return resp.data.downloadResult;
            }
        } catch (err: any) {
            // Do not throw error if downloading a logo
            if (fileType !== 0) {
                AlertService.addErrorAlert(err.response.data);
            }
            return false;
        }
    };

    async getAllNotificationTemplate(): Promise<NotificationTemplate[] | undefined> {
        try {
            const resp = await SchoolAPI.get("NotificationTemplate");
            return resp.data;
        } catch (err) {
            console.log(err);
            return;
        }
    }

    async addNewSchool(newSchool: NewSchool): Promise<boolean> {
        try {
            await SchoolAPI.post("NewSchool/addNewSchool/", {
                schoolCode: newSchool.schoolCode,
                templateSchoolCode: newSchool.templateSchoolCode,
                schoolName: newSchool.schoolName,
                initialAidYear: newSchool.initialAidYear,
                schoolPURL: newSchool.schoolPURL,
                processRCodes: newSchool.processRCodes,
                processCCodes: newSchool.processCCodes,
                systemMode: newSchool.systemMode,
                isV1Active: newSchool.isV1Active,
                isV2Active: newSchool.isV2Active,
                isV3Active: newSchool.isV3Active,
                isV4Active: newSchool.isV4Active,
                isV5Active: newSchool.isV5Active,
                isV6Active: newSchool.isV6Active
            });
            AlertService.addSuccessAlert("School has been added!");
            return true;
        } catch (err: any) {
            console.log(err);
            AlertService.addErrorAlert(err.response.data);
            return false;
        }
    }

    async getSchoolAuditInfo(dateFrom: Date, dateTo: Date): Promise<SchoolHistory[] | undefined> {
        try {
            const resp = await SchoolAPI.get(
                `/schoolHistory/getSchoolHistoryByDateRange/${dateFrom}/${dateTo}`
            );
            return resp.data;
        } catch (err) { }
    }

    async getSchoolConfigHistoryInfo(dateFrom: Date, dateTo: Date): Promise<SchoolConfigHistory[] | undefined> {
        try {
            const resp = await SchoolAPI.get(
                `/schoolConfigHistory/getSchoolConfigHistoryByDateRange/${dateFrom}/${dateTo}`
            );
            return resp.data;
        } catch (err) { }
    }

    async getSchoolIsirRecordHistoryInfo(dateFrom: Date, dateTo: Date): Promise<SchoolIsirRecordInfoHistory[] | undefined> {
        try {
            const resp = await SchoolAPI.get(
                `/schoolIsirRecordInfoHistory/getSchoolIsirRecordInfoHistoryByDateRange/${dateFrom}/${dateTo}`
            );
            return resp.data;
        } catch (err) { }
    }

    async getRolePermissionLinkHistory(dateFrom: Date, dateTo: Date): Promise<RolePermissionLinkHistory[] | undefined> {
        try {
            const resp = await SchoolAPI.get(
                `/rolePermissionLinkHistory/getRolePermissionLinkHistoryByDateRange/${dateFrom}/${dateTo}`
            );
            return resp.data;
        } catch (err) { }
    }

    async addorUpdateFTISetup(item: FTISetup): Promise<boolean> {
        try {
            const resp = await SchoolAPI.post("FTISetup/addOrUpdate/", item);
            return resp.data;
        } catch (err) {
            AlertService.addErrorAlert("Add/Update FTI Setup Failed");
            return false;
        }
    }

    searchCommentCodesByAidYear = async (aidYear: number):Promise<CommentCodeInfo[] | undefined> => {
        try {
            const resp = await SchoolAPI.post("commentCodeInfo/search/", {
                aidYear: aidYear,
                display: true
            });
            return resp.data;
        } catch (err) {
            console.log(err);
        }
       
    };

    updateAllCommentCodes = async (commentCodeInfos: CommentCodeInfo[]): Promise<boolean> => {
        try {
            const resp = await SchoolAPI.patch("commentCodeInfo/UpdateAll", commentCodeInfos);
            return resp.data;
        } catch (err) {
            console.log(err);
            return false;
        }
    };
       
}

let instance = new SchoolService();
export default instance;
